import type { CandlestickData } from 'lightweight-charts';
import { api } from './client';

export interface ApiPattern {
    type: string;
    direction: 'long' | 'short';
    index: number;
    time: string;
    price: number;
    points?: Array<{ time: string; value: number; line?: string }> | null;
}

export interface IndicatorPoint {
    time: number;       // unix seconds (конвертовано з ISO)
    rsi:   number | null;
    atr:   number | null;
    ema9:  number | null;
    ema21: number | null;
    ema200: number | null;
    supertrend:           number | null;
    supertrend_direction: number | null;  // 1 = bull, -1 = bear
}

export interface FuturesInfo {
    success: boolean;
    symbol: string;
    fundingRate: number;
    fundingRatePct: number;
    nextFundingTime: number;
    longShortRatio: number | null;
    longAccount: number | null;
    shortAccount: number | null;
}

export interface CandlesWithPatterns {
    candles: CandlestickData[];
    patterns: ApiPattern[];
    allowedPatterns: string[];
    indicators: IndicatorPoint[];
}

function parseCandles(raw: any[]): CandlestickData[] {
    return raw.map(c => ({
        time: (new Date(c.time).getTime() / 1000) as any,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
    }));
}

export async function fetchCandles(
    symbol: string,
    interval: string,
    limit: number
): Promise<CandlestickData[]> {
    const data = await api.get<any[]>(`/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`);
    return parseCandles(data);
}

export async function fetchCandlesWithPatterns(
    symbol: string,
    interval: string,
    limit: number,
    endTime?: number,
): Promise<CandlesWithPatterns> {
    const url = `/klines-with-patterns?symbol=${symbol}&interval=${interval}&limit=${limit}${endTime ? `&end_time=${endTime}` : ''}`;
    const data = await api.get<any>(url);
    if (!data.success) throw new Error(data.error ?? 'Failed to fetch candles');
    return {
        candles: parseCandles(data.candles),
        patterns: data.patterns_candlestick as ApiPattern[],
        allowedPatterns: data.allowed_patterns as string[],
        indicators: (data.indicators ?? []).map((d: any): IndicatorPoint => ({
            time:                 new Date(d.time).getTime() / 1000,
            rsi:                  d.rsi    ?? null,
            atr:                  d.atr    ?? null,
            ema9:                 d.ema9   ?? null,
            ema21:                d.ema21  ?? null,
            ema200:               d.ema200 ?? null,
            supertrend:           d.supertrend           ?? null,
            supertrend_direction: d.supertrend_direction ?? null,
        })),
    };
}

export async function fetchFuturesInfo(symbol: string): Promise<FuturesInfo | null> {
    try {
        return await api.get<FuturesInfo>(`/futures-info?symbol=${symbol}`);
    } catch {
        return null;
    }
}
